import { message } from "antd";
import { ofType } from "redux-observable";
import { concat, EMPTY, from, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import i18n from "@/locales/i18n";
import { startLoading, stopLoading } from "../../../../store/loading";
import type { RootEpic } from "../../../../store/types";
import { storageActions } from "./storageSlice";
import { StorageService } from "../services/storageApi";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  wouldExceedSoftLimit,
  calculateExceededAmount,
  calculateRemainingCapacity,
  ALLOW_OVER_CAPACITY_RATIO,
  getCapacityConfigInfo,
} from "@/config/storageCapacity.config";
interface UpdateLimitOptimisticBatchPayload {
  entityIds: string[];
  newLimitGB: number;
}

/**
 * EPIC 1: Fetch Storage Entities (Initial Load)
 * Responsibility: Load first page of storage entities with filters
 */
export const fetchStorageEntitiesEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.fetchStorageEntitiesRequest.type),
    switchMap((action: any) => {
      const { search, type, select, orderBy } = action.payload;
      
      // Get companyId from state or localStorage
      const companyId = localStorage.getItem("companyId") || "";
      
      if (!companyId) {
        return of(
          storageActions.fetchStorageEntitiesFailure(
            "Company ID not found"
          )
        );
      }

      return concat(
        of(startLoading({ key: "fetchStorageEntities" })),
        StorageService.fetchStorageEntities(companyId, {
          filter: type || undefined,
          select: select || undefined,
          orderBy: orderBy || undefined,
          page: 1,
          pageSize: 20,
          search: search || undefined,
        }).pipe(
          switchMap((response) => {
            console.log("✓ Storage entities loaded:", response.results.length);
            return [
              storageActions.fetchStorageEntitiesSuccess({
                results: response.results,
                page: response.page,
                pageSize: response.pageSize,
                queryCount: response.queryCount,
                pageCount: response.pageCount,
                firstRowIndex: response.firstRowIndex,
                lastRowIndex: response.lastRowIndex,
              }),
            ];
          }),
          catchError((err) => {
            console.error("Error fetching storage entities:", err);
            message.error(
              err.message || "Failed to load storage data"
            );
            return of(
              storageActions.fetchStorageEntitiesFailure(
                err.message || "Error fetching storage entities"
              )
            );
          })
        ),
        of(stopLoading({ key: "fetchStorageEntities" }))
      );
    })
  );

/**
 * EPIC 2: Fetch Next Page (Lazy Load)
 * Responsibility: Load next page when user scrolls to bottom
 */
export const fetchNextPageEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(storageActions.fetchNextPageRequest.type),
    switchMap(() => {
      const state = state$.value as any;
      const { currentPage, hasMore } = state.storage.pagination;
      const { search, type, select, orderBy } = state.storage.filters;

      console.log("📄 Fetching next page:", { currentPage, hasMore, nextPage: currentPage + 1 });

      // Don't fetch if no more pages
      if (!hasMore) {
        console.log("⚠️ No more pages to load");
        return EMPTY;
      }

      const companyId = localStorage.getItem("companyId") || "";
      
      if (!companyId) {
        return of(
          storageActions.fetchNextPageFailure(
            "Company ID not found"
          )
        );
      }

      const nextPage = currentPage + 1;

      return concat(
        of(startLoading({ key: "fetchNextPage" })),
        StorageService.fetchStorageEntities(companyId, {
          filter: type || undefined,
          select: select || undefined,
          orderBy: orderBy || undefined,
          page: nextPage,
          pageSize: 20,
          search: search || undefined,
        }).pipe(
          switchMap((response) => {
            console.log("✓ Next page loaded:", response.results.length, "items");
            return [
              storageActions.fetchNextPageSuccess({
                results: response.results,
                page: response.page,
                pageSize: response.pageSize,
                queryCount: response.queryCount,
                pageCount: response.pageCount,
                firstRowIndex: response.firstRowIndex,
                lastRowIndex: response.lastRowIndex,
              }),
            ];
          }),
          catchError((err) => {
            console.error("Error fetching next page:", err);
            return of(
              storageActions.fetchNextPageFailure(
                err.message || "Error fetching next page"
              )
            );
          })
        ),
        of(stopLoading({ key: "fetchNextPage" }))
      );
    })
  );

/**
 * EPIC 3: Update Storage Limit (Batch with Optimistic Updates)
 * Responsibility: Update capacity limit for multiple entities
 */
export const updateStorageLimitBatchEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.updateLimitOptimisticBatch.type),
    switchMap((action: PayloadAction<UpdateLimitOptimisticBatchPayload>) => {
      const { entityIds, newLimitGB } = action.payload;

      // API call without loading state (optimistic update already shown)
      return StorageService.updateStorageLimitBatch(entityIds, newLimitGB).pipe(
        switchMap((updatedEntities) => {
          console.log("✓ Storage limits updated:", updatedEntities.length);
          
          // Check if all items were successful (compare with requested count)
          const successCount = updatedEntities.length;
          const failedCount = entityIds.length - successCount;
          
          if (failedCount > 0) {
            // Partial failure
            const successIds = updatedEntities.map(e => e.id);
            const failedIds = entityIds.filter(id => !successIds.includes(id));
            
            message.warning(
              `Updated ${successCount}/${entityIds.length} item(s). ${failedCount} item(s) failed.`
            );
            
            return [
              // Confirm success for successful items
              storageActions.updateLimitSuccessBatch({
                updatedEntities,
              }),
              // Rollback failed items
              storageActions.updateLimitFailureBatch({
                entityIds: failedIds,
                error: 'Some items failed to update',
              }),
            ];
          } else {
            // All successful
            message.success(`Successfully updated ${successCount} item(s)`);
            return [
              storageActions.updateLimitSuccessBatch({
                updatedEntities,
              }),
            ];
          }
        }),
        catchError((err) => {
          console.error("Error updating storage limits:", err);
          message.error(
            err.message || "Failed to update capacity limits"
          );
          // Rollback all items on API error
          return of(
            storageActions.updateLimitFailureBatch({
              entityIds,
              error: err.message || "Error updating limits",
            })
          );
        })
      );
    })
  );

/**
 * EPIC 4: Init Storage Management (Optional)
 * Responsibility: Initialize storage management with default filters
 */
export const initStorageManagementEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.initStorageManagement.type),
    switchMap((action: any) => {
      const { filters } = action.payload || {};
      
      return [
        storageActions.fetchStorageEntitiesRequest(
          filters || {
            search: '',
            type: '',
            status: '',
            select: 'usage',
            orderBy: 'desc',
          }
        ),
      ];
    })
  );

/**
 * EPIC 5: Apply Filters (Trigger Refetch)
 * Responsibility: When user applies new filters, refetch from page 1
 */
export const applyFiltersEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.applyFilters.type),
    switchMap((action: any) => {
      return [
        storageActions.fetchStorageEntitiesRequest(action.payload),
      ];
    })
  );

/**
 * EPIC 6: Reset Filters (Back to Default)
 * Responsibility: Reset filters and refetch with defaults
 */
export const resetFiltersEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.resetFilters.type),
    switchMap(() => {
      return [
        storageActions.fetchStorageEntitiesRequest({
          search: '',
          type: '',
          status: '',
          select: 'usage',
          orderBy: 'desc',
        }),
      ];
    })
  );

/**
 * EPIC 7: Check Storage Capacity Before Upload
 * Responsibility: Validate if upload size exceeds storage limits with soft limit (10% over capacity)
 * This epic runs BEFORE any upload actions
 * 
 * Soft Limit Policy:
 * - Allows upload to exceed capacity by 10% (configurable via ALLOW_OVER_CAPACITY_RATIO)
 * - Blocks upload only when exceeding capacity by more than 10%
 */
export const checkStorageCapacityEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(storageActions.checkStorageCapacityBeforeUpload.type),
    switchMap((action: any) => {
      const { totalUploadSize, isTeamDrive, uploadContext } = action.payload;

      // ===== 1. GET STORAGE INFO =====
      const rootState = state$.value as any;

      const userStorageSize =
        rootState.file?.userStorage?.storageSize ?? 0;
      const userCapacitySize =
        rootState.file?.userCapacitySize ?? null;

      const companyStorageSize =
        rootState.uploadFile?.companyStorageResponse?.storageSize ?? null;
      const companyCapacitySize =
        rootState.uploadFile?.companyCapacitySize ?? null;

      const userRemaining =
        userCapacitySize == null
          ? Infinity
          : calculateRemainingCapacity(userStorageSize, userCapacitySize);

      const companyRemaining =
        companyStorageSize == null
          ? Infinity
          : calculateRemainingCapacity(companyStorageSize, companyCapacitySize);

      const isUserUnlimited = userCapacitySize == null;
      const isCompanyUnlimited = companyStorageSize == null;

      console.log('📊 Storage Capacity Check:', {
        totalUploadSize,
        isTeamDrive,
        userStorageSize,
        userCapacitySize,
        companyStorageSize,
        companyCapacitySize,
        isUserUnlimited,
        isCompanyUnlimited,
      });

      // ===== 2. UNLIMITED CASE =====
      if (isUserUnlimited && isCompanyUnlimited) {
        console.log('♾️ Unlimited storage (USER + COMPANY) → allow upload');

        const actions: any[] = [
          storageActions.storageCapacityCheckSuccess({
            totalUploadSize,
            userRemaining: Infinity,
            companyRemaining: Infinity,
          }),
        ];

        if (uploadContext) {
          actions.push(
            storageActions.proceedUpload({ uploadContext })
          );
        }

        return from(actions);
      }

      // ===== 3. COMPANY UNLIMITED → CHECK USER ONLY =====
      if (isCompanyUnlimited) {
        const userExceeded = wouldExceedSoftLimit(
          userStorageSize,
          totalUploadSize,
          userCapacitySize
        );

        if (userExceeded) {
          const exceededBy = calculateExceededAmount(
            userStorageSize,
            totalUploadSize,
            userCapacitySize
          );

          message.error(i18n.t('storage:error_personal_storage_exceeded'));

          return of(
            storageActions.storageCapacityExceeded({
              exceededType: 'USER',
              exceededBy,
              totalUploadSize,
              userRemaining,
              companyRemaining: Infinity,
              message: i18n.t('storage:message_personal_exceeded', {
                size: formatBytes(totalUploadSize),
              }),
            })
          );
        }
      }

      // ===== 4. COMPANY EXISTS =====
      if (!isCompanyUnlimited) {
        // --- Personal Drive ---
        if (!isTeamDrive) {
          const userExceeded =
            !isUserUnlimited &&
            wouldExceedSoftLimit(
              userStorageSize,
              totalUploadSize,
              userCapacitySize
            );

          const companyExceeded = wouldExceedSoftLimit(
            companyStorageSize,
            totalUploadSize,
            companyCapacitySize
          );

          if (userExceeded && companyExceeded) {
            const userExceededBy = calculateExceededAmount(
              userStorageSize,
              totalUploadSize,
              userCapacitySize
            );

            const companyExceededBy = calculateExceededAmount(
              companyStorageSize,
              totalUploadSize,
              companyCapacitySize
            );

            const maxExceededBy = Math.max(
              userExceededBy,
              companyExceededBy
            );

            message.error(
              i18n.t('storage:error_storage_limit_exceeded')
            );

            return of(
              storageActions.storageCapacityExceeded({
                exceededType: 'BOTH',
                exceededBy: maxExceededBy,
                totalUploadSize,
                userRemaining,
                companyRemaining,
                message: i18n.t(
                  'storage:message_both_exceeded',
                  { size: formatBytes(totalUploadSize) }
                ),
              })
            );
          }

          if (userExceeded) {
            const exceededBy = calculateExceededAmount(
              userStorageSize,
              totalUploadSize,
              userCapacitySize
            );

            message.error(
              i18n.t('storage:error_personal_storage_exceeded')
            );

            return of(
              storageActions.storageCapacityExceeded({
                exceededType: 'USER',
                exceededBy,
                totalUploadSize,
                userRemaining,
                companyRemaining,
                message: i18n.t(
                  'storage:message_personal_exceeded',
                  { size: formatBytes(totalUploadSize) }
                ),
              })
            );
          }

          if (companyExceeded) {
            const exceededBy = calculateExceededAmount(
              companyStorageSize,
              totalUploadSize,
              companyCapacitySize
            );

            message.error(
              i18n.t('storage:error_company_storage_exceeded')
            );

            return of(
              storageActions.storageCapacityExceeded({
                exceededType: 'COMPANY',
                exceededBy,
                totalUploadSize,
                userRemaining,
                companyRemaining,
                message: i18n.t(
                  'storage:message_company_exceeded',
                  { size: formatBytes(totalUploadSize) }
                ),
              })
            );
          }
        }
        // --- Team Drive ---
        else {
          const companyExceeded = wouldExceedSoftLimit(
            companyStorageSize,
            totalUploadSize,
            companyCapacitySize
          );

          if (companyExceeded) {
            const exceededBy = calculateExceededAmount(
              companyStorageSize,
              totalUploadSize,
              companyCapacitySize
            );

            message.error(
              i18n.t('storage:error_company_storage_exceeded')
            );

            return of(
              storageActions.storageCapacityExceeded({
                exceededType: 'COMPANY',
                exceededBy,
                totalUploadSize,
                userRemaining,
                companyRemaining,
                message: i18n.t(
                  'storage:message_team_drive_exceeded',
                  { size: formatBytes(totalUploadSize) }
                ),
              })
            );
          }
        }
      }

      // ===== 5. PASS → ALLOW UPLOAD =====
      console.log(
        `✅ Storage capacity check passed (soft limit ${ALLOW_OVER_CAPACITY_RATIO * 100}%)`
      );

      const actions: any[] = [
        storageActions.storageCapacityCheckSuccess({
          totalUploadSize,
          userRemaining,
          companyRemaining,
        }),
      ];

      if (uploadContext) {
        actions.push(
          storageActions.proceedUpload({ uploadContext })
        );
      }

      return from(actions);
    })
  );

/**
 * Helper function to format bytes to human-readable format
 */
function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Export all storage epics as an array
 */
export const storageEpics = [
  fetchStorageEntitiesEpic$,
  fetchNextPageEpic$,
  updateStorageLimitBatchEpic$,
  initStorageManagementEpic$,
  applyFiltersEpic$,
  resetFiltersEpic$,
  checkStorageCapacityEpic$,
];
